import React from "react";
import { withStyles, createStyles, WithStyles, Theme } from "@material-ui/core/styles";
import { RouteComponentProps } from "react-router-dom";

import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";
import orange from "@material-ui/core/colors/orange";
import { Row } from "react-data-grid";

import Splitter from "m-react-splitters";
import "m-react-splitters/lib/splitters.css";

import Grid from "../../lib/components/grid";
import { EDIValidationGrid } from "./importvalidationgrid";
import { ColumnsFileImportHeaderInfo } from "../importinfo/importinfogridsetupdetail";

import {
  getFileImport,
  getFileImportHeaderInfo,
  getFileImportHeaderInfoValidation,
  updateFileImportHeaderInfo,
  execProcessIntake,
  execProcessDispatch,
  getFileImportByStatus,
  FileImportStatus,
} from "../../lib/api/FileImport";
import { commoditiesAllSortedMappedforCombo } from "../../lib/api/commodity";
import { VarietiesByCommodityMappedForComboValue } from "../../lib/api/variety";
import { getProducerReadyForComboMappedForComboValue } from "../../lib/api/producer";

import { SnackContext } from "../../lib/context/SnackContext";
import { GenerateErrorMessage } from "../../lib/helpers/string_methods";
import { isNullOrUndef } from "../../lib/helpers/isNullOrUndef";

const styles = (theme: Theme) =>
  createStyles({
    root: {
      height: "100%",
      width: "100%",
      display: "flex",
      flexDirection: "column",
    },
    toolbar: {
      display: "flex",
      flexDirection: "row",
      height: "70px",
      alignItems: "center",
      justifyContent: "flex-start",
      padding: "10px",
      gap: "10px",
    },
    processAllButton: {
      color: "white",
      backgroundColor: orange[700],
      "&:hover": { backgroundColor: orange[900] },
    },
    selectedHeader: {
      color: "white",
      backgroundColor: theme.palette.primary.main,
    },
    dirtyDot: {
      color: "#ff4081",
      fontWeight: "bold",
      marginLeft: "8px",
    },
  });

type EDIValidationProps = { snack: React.ContextType<typeof SnackContext> } & WithStyles<typeof styles> & RouteComponentProps;

type FileRow = {
  id: number;
  filename: string;
  status: string;
  [key: string]: any;
};

type DetailRow = {
  id: number;
  FileImport_id: number;
  edivalidationerrors?: string;
  [key: string]: any;
};

class EDIValidationUnstyled extends React.Component<EDIValidationProps, {}> {
  state = {
    files: [] as FileRow[],
    selectedFile: undefined as FileRow | undefined,
    rows: [] as DetailRow[],
    rowsValidation: [] as any[],
    dirtyMap: {} as Record<number, boolean>,
    editedRows: {} as Record<number, DetailRow[]>,
    commoditiesCombo: [] as any[],
    varietiesByCommodity: [] as any[],
    producersCombo: [] as any[],
    commoditiesComboObj: {},
    producersComboObj: {},
    loading: false,
    saving: false,
    processing: false,
    minGridHeight: 0,
    minGridHeightSecond: 0,
  };

  componentDidMount() {
    this.setGridHeights();
    this.loadImportedFiles();
    this.loadCommodities();
  }

  loadImportedFiles = async () => {
    try {
      this.setState({ loading: true });

      const result = await getFileImportByStatus(FileImportStatus.Imported);
      this.setState({ files: result.data || [] }, () => this.setGridHeights());
    } catch (error) {
      const err = GenerateErrorMessage(error, "Error loading imported files");
      this.props.snack.updateSnack({ show: true, color: "red", message: err });
    } finally {
      this.setState({ loading: false });
    }
  };

  setGridHeights = () => {
    const primarysplit = document.getElementsByClassName("splitter horizontal");
    if (primarysplit && primarysplit[0] && primarysplit[0].children && primarysplit[0].children[0]) {
      this.setState({
        minGridHeight: primarysplit[0].children[0].clientHeight - 70,
        minGridHeightSecond: primarysplit[0].children[2].clientHeight - 250,
      });
    }
  };

  loadCommodities = async () => {
    try {
      const commodities = await commoditiesAllSortedMappedforCombo();
      this.setState({ commoditiesCombo: commodities });
    } catch (error) {
      const err = GenerateErrorMessage(error, "Error loading commodities");
      this.props.snack.updateSnack({ show: true, color: "red", message: err });
    }
  };

  getVarietiesByCommodity = async (commdityCode) => {
    try {
      if (commdityCode && commdityCode.length != 0) {
        const varieties = await VarietiesByCommodityMappedForComboValue(commdityCode).catch((error) => {
          const err = GenerateErrorMessage(error, "Error retrieving data - getVarietiesByCommodity");
          this.props.snack.updateSnack({ show: true, color: "red", message: err });
        });
        if (varieties && varieties.length <= 0) {
          this.setState({ commoditiesComboObj: { ...this.state.commoditiesComboObj, [commdityCode]: [{ id: "", value: "", display: "" }] } });
        } else {
          this.setState({ commoditiesComboObj: { ...this.state.commoditiesComboObj, [commdityCode]: varieties } });
        }
      }
    } catch (error) {
      const err = GenerateErrorMessage(error, "Error retrieving data - getVarietiesByCommodity");
      this.props.snack.updateSnack({ show: true, color: "red", message: err });
    }
  };

  getProducersByPuc = async (puc) => {
    try {
      const producers = await getProducerReadyForComboMappedForComboValue(puc);
      if (producers.length <= 0) {
        this.setState({ producersComboObj: { ...this.state.producersComboObj, [puc]: [{ id: "", value: "", data: [] }] } });
      } else {
        this.setState({ producersComboObj: { ...this.state.producersComboObj, [puc]: producers } });
      }
    } catch (error) {
      this.props.snack.updateSnack({ show: true, color: "red", message: error });
    }
  };

  handleFileDoubleClick = async (row: FileRow) => {
    if (this.state.selectedFile && this.state.selectedFile.id === row.id) {
      this.setState({ selectedFile: undefined, rows: [], rowsValidation: [] });
      return;
    }

    try {
      this.setState({ loading: true });

      const [headerInfo, headerInfoValidation] = await Promise.all([getFileImportHeaderInfo(row.id), getFileImportHeaderInfoValidation(row.id)]);

      const formatted: DetailRow[] = (headerInfo.data || []).map((r: any) => {
        const errors = (headerInfoValidation.data || []).reduce((str: string, vRow: any) => {
          if (vRow.rowId == r.id) {
            str = str !== "" ? `${str}, ${vRow.column}` : vRow.column;
          }
          return str;
        }, "");
        return { ...r, edivalidationerrors: errors };
      });

      // checks if there's any edited rows in this file
      const localEdited = this.state.editedRows[row.id];
      const rowsToShow = localEdited && localEdited.length > 0 ? localEdited : formatted;

      this.setState({
        selectedFile: row,
        rows: rowsToShow,
        rowsValidation: headerInfoValidation.data || [],
        loading: false,
      });

      if (formatted[0] && formatted[0].FarmNumber) {
        this.getProducersByPuc(formatted[0].FarmNumber);
      }
      if (formatted[0] && formatted[0].CommodityCode) {
        this.getVarietiesByCommodity(formatted[0].CommodityCode);
      }

      this.setGridHeights();
    } catch (error) {
      const err = GenerateErrorMessage(error, "Error loading file rows");
      this.props.snack.updateSnack({ show: true, color: "red", message: err });
      this.setState({ loading: false });
    }
  };

  handleRowsDetailUpdated = (_: any, rows: DetailRow[], data: any) => {
    const dataRows = this.state.rows.slice();

    const updated = rows.reduce((arr, row) => {
      const idx = arr.findIndex((item) => item.id === row.id);
      if (idx >= 0) {
        if (data && data.column && data.column.key === "CommodityCode") {
          row = { ...row, VarietyCode: "" };
        }
        if (data && data.column && data.column.key === "ProducerID") {
          const findProducer = this.state.producersCombo.find((prod) => prod.data && prod.data.code == row.ProducerID);
          if (findProducer) {
            row = { ...row, ProducerName: findProducer.data.name };
          }
        }
        arr[idx] = { ...arr[idx], ...row };
      }
      return arr;
    }, dataRows);

    const fileId = this.state.selectedFile && this.state.selectedFile.id;
    const dirtyMap = { ...this.state.dirtyMap };
    const editedRows = { ...this.state.editedRows };

    if (fileId != null) {
      dirtyMap[fileId] = true;
      editedRows[fileId] = updated;
    }

    this.setState({ rows: updated, dirtyMap, editedRows });
  };

  handleProcessAll = async () => {
    const dirtyIds = Object.keys(this.state.dirtyMap)
      .filter((id) => this.state.dirtyMap[Number(id)])
      .map(Number);

    if (dirtyIds.length === 0) return;

    this.setState({ processing: true });

    const processedIds: number[] = [];
    const failedIds: number[] = [];

    for (const fileId of dirtyIds) {
      try {
        const edited = this.state.editedRows[fileId];
        if (edited && edited.length > 0) {
          const payload = edited
            .filter((r) => r.id)
            .map((r) => {
              const clone: any = { ...r };
              delete clone["edivalidationerrors"];
              clone.rowId = clone.id;
              delete clone.id;
              clone.tableName = "FileImportHeaderInfo";
              return clone;
            });
          await updateFileImportHeaderInfo(payload);
        }

        const file = this.state.files.find((f) => f.id === fileId);
        if (!file) continue;

        await execProcessIntake(fileId);

        if (!isNullOrUndef(file.fileType) && String(file.fileType).toLowerCase() !== "intake") {
          try {
            await execProcessDispatch(fileId);
          } catch (e) {}
        }

        processedIds.push(fileId);
      } catch (error) {
        failedIds.push(fileId);
      }
    }

    const dirtyMap = { ...this.state.dirtyMap };
    processedIds.forEach((id) => delete dirtyMap[id]);

    const editedRows = { ...this.state.editedRows };
    processedIds.forEach((id) => delete editedRows[id]);

    const selectedWasProcessed = this.state.selectedFile && processedIds.indexOf(this.state.selectedFile.id) !== -1;

    this.setState({
      dirtyMap,
      editedRows,
      processing: false,
      ...(selectedWasProcessed ? { selectedFile: undefined, rows: [], rowsValidation: [] } : {}),
    });

    if (failedIds.length === 0) {
      this.props.snack.updateSnack({
        show: true,
        color: "green",
        message: `Processed ${processedIds.length} file(s)`,
      });
    } else if (processedIds.length === 0) {
      this.props.snack.updateSnack({
        show: true,
        color: "red",
        message: `Failed to process ${failedIds.length} file(s)`,
      });
    } else {
      this.props.snack.updateSnack({
        show: true,
        color: "orange",
        message: `Processed ${processedIds.length}, failed ${failedIds.length}`,
      });
    }

    await this.loadImportedFiles();
  };

  render() {
    const { classes } = this.props;
    const { files, rows, selectedFile, dirtyMap, loading, saving, processing } = this.state;
    const decoratedFiles = files.map((f) => {
      const isDirty = !!dirtyMap[f.id];
      return {
        ...f,
        isSelected: selectedFile && selectedFile.id === f.id,
        isDirty: isDirty,
        statusLabel: isDirty ? "Updated" : "Not updated",
      };
    });

    return (
      <div className={classes.root}>
        <div id="main_splitter_div" style={{ height: "100%", width: "100%", display: "grid" }}>
          <Splitter
            postPoned
            dispatchResize
            position="horizontal"
            primaryPaneMaxHeight="calc(100% - 150px)"
            primaryPaneHeight="calc(100% - 600px)"
            primaryPaneWidth="100%"
            onDragFinished={() => this.setGridHeights()}
          >
            <div id="primary_grid" style={{ marginLeft: "1px", position: "relative" }}>
              <div style={{ position: "absolute", top: 15, left: 10, zIndex: 10 }}>
                <Button variant="contained" className={classes.processAllButton} onClick={this.handleProcessAll} disabled={Object.keys(dirtyMap).length === 0 || processing}>
                  Process All
                  {processing && <CircularProgress size={14} style={{ marginLeft: 8 }} />}
                </Button>
              </div>
              <Grid
                fill
                data={decoratedFiles}
                clearFilters={"edivalidation"}
                GridColumns={(data, filters, arrangement, columnsWidth) => EDIValidationGrid(data, filters, arrangement, columnsWidth)}
                forceHeight={this.state.minGridHeight}
                rowRenderer={(props) => <Row {...props} onDoubleClickCapture={() => this.handleFileDoubleClick(props.row)} />}
              />
            </div>

            <div id="secondary_grid">
              {selectedFile && (
                <div className={classes.selectedHeader}>
                  <Typography style={{ margin: "20px" }}>
                    {selectedFile.filename}
                    {dirtyMap[selectedFile.id] && <span className={classes.dirtyDot}>●</span>}
                  </Typography>
                </div>
              )}
              <Grid
                fill
                showFilterChips
                data={rows}
                loading={loading}
                clearFilters={"edivalidationdetail"}
                countRowColumns={["edivalidationerrors"]}
                handleChange={this.handleRowsDetailUpdated}
                forceHeight={this.state.minGridHeightSecond}
                totalRowColumns={["PalletSize", "NoCartons", "GrossWeight", "Weight"]}
                GridColumns={(data, filters, arrangement, columnsWidth) =>
                  ColumnsFileImportHeaderInfo(
                    data,
                    filters,
                    arrangement,
                    columnsWidth,
                    this.state.commoditiesCombo,
                    this.state.varietiesByCommodity,
                    this.state.rowsValidation,
                    this.state.producersCombo,
                  )
                }
              />
            </div>
          </Splitter>
        </div>
      </div>
    );
  }
}

const EDIValidationWithSnack = (props: Omit<EDIValidationProps, "snack">) => {
  const snack = React.useContext(SnackContext);
  return <EDIValidationUnstyled {...props} snack={snack} />;
};

export default withStyles(styles)(EDIValidationWithSnack);
